<template>
    <div class="dialog draggable-item bg-light light w-100-sm w-75-md w-50-lg">
        <div class="dialog-title pl-10">
            {{ $t("Manage customer groups") }}
            <span class="button square closer" @click="close"></span>
        </div>
        <div class="dialog-content overflow">
            <div class="form-group">
                <label class="text-bold mb-1 d-block">{{ $t("Customer") }}</label>
                <select class="input w-100" v-model="selectedCustomerId" :disabled="loadingCustomers">
                    <option value="">
                        {{ loadingCustomers ? $t("Loading customer...") : $t("Select a customer") }}
                    </option>
                    <option v-for="customer in customers" :key="customer.id" :value="String(customer.id)">
                        {{ customer.company_name }}
                    </option>
                </select>
                <div v-if="!loadingCustomers && customers.length === 0" class="text-small fg-dark mt-2">
                    {{ $t("No customers available") }}
                </div>
            </div>

            <div class="form-group mt-4">
                <label class="text-bold mb-1 d-block">{{ $t("Assigned groups") }}</label>
                <div v-if="!selectedCustomerId" class="text-small fg-dark mb-2">
                    {{ $t("Select a customer first") }}
                </div>
                <div v-else-if="availableGroups.length === 0" class="text-small fg-dark mb-2">
                    {{ $t("No groups available") }}
                </div>
                <div v-else :key="selectedCustomerId">
                    <select data-role="select" data-filter="true" multiple
                        :data-filter-placeholder="$t('Search for groups')"
                        data-cls-selected-item="bg-light border pr-2 rounded"
                        data-cls-selected-item-remover="bg-transparent border-0 d-flex flex-align-center"
                        class="input" ref="groupsSelect" @change="onGroupsChange">
                        <option v-for="group in availableGroups" :key="group.id_group"
                            :value="String(group.id_group)"
                            :selected="isGroupSelected(group.id_group)">
                            {{ group.name_group }}
                        </option>
                    </select>
                </div>
            </div>
        </div>

        <div class="dialog-actions p-3 border-top text-right">
            <button class="button" @click="close">{{ $t("Cancel") }}</button>
            <button class="button secondary ml-2" :disabled="!selectedCustomer || saving" @click="save">
                {{ $t("Save") }}
            </button>
        </div>
    </div>
</template>

<script>
export default {
    name: "AssignGroupsToCustomers",
    data() {
        return {
            customers: [],
            groups: [],
            availableGroups: [],
            selectedCustomerId: "",
            selectedCustomer: null,
            selectedGroupIds: [],
            loadingCustomers: true,
            saving: false
        };
    },
    watch: {
        selectedCustomerId() {
            this.loadCustomerGroups();
        }
    },
    async created() {
        this.groups = this.$session.getGroups() || [];
        const customers = await this.$api.getCustomers();
        this.customers = Array.isArray(customers) ? customers : [];
        this.loadingCustomers = false;
    },
    methods: {
        normalizeGroupIds(values) {
            if (!Array.isArray(values)) return [];

            return values
                .map(value => {
                    if (value && typeof value === "object") {
                        return value.id_group != null ? value.id_group : value.id;
                    }
                    return value;
                })
                .filter(value => value != null)
                .map(value => String(value));
        },
        isGroupSelected(idGroup) {
            return this.selectedGroupIds.includes(String(idGroup));
        },
        onGroupsChange(event) {
            this.selectedGroupIds = Array.from(event.target.selectedOptions)
                .map(option => String(option.value));
        },
        loadCustomerGroups() {
            const customerId = this.selectedCustomerId;
            this.selectedCustomer = this.customers.find(customer => String(customer.id) === customerId) || null;

            if (!this.selectedCustomer) {
                this.availableGroups = [];
                this.selectedGroupIds = [];
                return;
            }

            const customerGroupIds = this.normalizeGroupIds(this.selectedCustomer.ids_groups);
            const sessionGroupIds = this.groups
                .filter(group => String(group.id_customer) === customerId)
                .map(group => String(group.id_group));
            const assignedGroupIds = [...new Set([...customerGroupIds, ...sessionGroupIds])];

            this.availableGroups = this.groups.filter(group =>
                group.id_customer == null ||
                group.id_customer === "" ||
                String(group.id_customer) === customerId ||
                assignedGroupIds.includes(String(group.id_group))
            );

            const availableGroupIds = new Set(
                this.availableGroups.map(group => String(group.id_group))
            );
            this.selectedGroupIds = assignedGroupIds.filter(idGroup => availableGroupIds.has(idGroup));
        },
        close() {
            this.$root.$emit("CLOSEASSIGNGROUPSTOCUSTOMERSDIALOG");
        },
        async save() {
            if (!this.selectedCustomer || this.saving) return;

            this.saving = true;
            try {
                const saved = await this.$api.editCustomer({
                    ...this.selectedCustomer,
                    ids_groups: [...this.selectedGroupIds]
                });
                if (saved == null) return;

                const groups = await this.$api.getGroups();
                if (Array.isArray(groups)) this.$session.setGroups(groups);

                this.$root.$emit("refreshGroups");
                this.$root.$emit("refreshCustomers");
                this.close();
            } finally {
                this.saving = false;
            }
        }
    }
};
</script>